import { post } from "@common/api/apiShared.ts";
import { downloadFile } from "@common/api/downloadFile.ts";
import { getExportFileName } from "@common/exportFileName";
import { useTranslation } from "../../localization/useTranslation.ts";
import { ContractExportHeader, ContractTableRequest } from "../../contract/Contracts.types.ts";
import { ActivitiesSearchRequest, ActivityExportHeader } from "../../annualWheel/activities/ActivitiesSearch.types.ts";
import { SystemExportHeader, SystemTableRequest } from "../../system/systems/Systems.types.ts";
import { PolicyExportHeader, PolicyTableRequest } from "../../policy/policies/Policies.types.ts";
import { RiskOperationalExportHeader, RiskOperationalTableRequest } from "../../risk/riskOperationalOverview/RiskOperationalOverview.types.ts";
import { RiskMitigationExportHeader, RiskMitigationFilterRequest } from "../../risk/riskMitigationOverview/RiskMitigationOverview.types.ts";
import { RiskAssessmentExportHeader, RiskAssessmentExportRequest } from "../../risk/riskAssessmentsOverview/RiskAssessmentsOverview.types.ts";
import { UserExportHeader } from "../../user/User.types.ts";
import { FrameworkExportHeader } from "../../framework/frameworks/Frameworks.types.ts";
import { ObservationExportHeader } from "../../risk/riskObservation/RiskObservations.types";
import { LegalEntityExportHeader, LegalEntitySearchRequest } from "../../legalEntity/legalEntities/LegalEntities.types.ts";
import { IncidentLogExportHeader, IncidentLogSearchRequest } from "../../incidentLog/incidentLogs/IncidentLogs.types.ts";
import { ProcessExportHeader, ProcessSearchRequest } from "../../process/Process.types.ts";
import { ProcessingActivityExportHeader, ProcessingActivitySearchRequest } from "../../processingActivity/processingActivities/ProcessingActivities.types.ts";
import { SharingExportHeader, SharingSearchRequest } from "../../sharings/Sharings.types.ts";

export function useCustomListExport() {
    const { translateDate } = useTranslation();

    let exportListInstantly = async (listId: string, listName: string) => {
        const date = new Date();
        const fileName = `${listName} - ${translateDate(date)} ${date.getHours().toString().padStart(2)}_${date.getMinutes().toString().padStart(2)}.xlsx`;
        await downloadFile(`/CustomListExport/${listId}/export`, fileName, "POST");
    };

    let exportListInBackground = (listId: string) => {
        return post<{ id: string }>(`/CustomListExport/${listId}`, {});
    };

    let exportAllDataInBackground = () => {
        return post(`/full-data-export/export`, { contracts: true });
    };

    let exportContractsInBackground = (reportName: string, headers: Array<ContractExportHeader>, contractsTableRequest: ContractTableRequest) => {
        return post(`/CustomListExport/ContractExport`, getContractExportBody(reportName, headers, contractsTableRequest));
    };

    const exportActivitiesInBackground = (reportName: string, headers: Array<ActivityExportHeader>, activitiesSearchRequest: ActivitiesSearchRequest) => {
        return post(`/CustomListExport/ActivityExport`, getActivityExportBody(reportName, headers, activitiesSearchRequest));
    };

    const exportSystemsInBackground = (reportName: string, headers: Array<SystemExportHeader>, systemTableRequest: SystemTableRequest) => {
        return post(`/CustomListExport/SystemExport`, getSystemExportBody(reportName, headers, systemTableRequest));
    };

    const exportPoliciesInBackground = (reportName: string, headers: Array<PolicyExportHeader>, policyTableRequest: PolicyTableRequest) => {
        return post(`/CustomListExport/PolicyExport`, getPolicyExportBody(reportName, headers, policyTableRequest));
    };

    const exportRiskOperationalInBackground = (
        reportName: string,
        headers: Array<RiskOperationalExportHeader>,
        riskOperationalTableRequest: RiskOperationalTableRequest
    ) => {
        return post(`/CustomListExport/RiskOperationalExport`, getRiskOperationalExportBody(reportName, headers, riskOperationalTableRequest));
    };

    const exportRiskMitigationInBackground = (
        reportName: string,
        headers: Array<RiskMitigationExportHeader>,
        riskMitigationTableRequest: RiskMitigationFilterRequest
    ) => {
        return post(`/CustomListExport/RiskMitigationExport`, getRiskMitigationExportBody(reportName, headers, riskMitigationTableRequest));
    };

    const exportRiskAssessmentInBackground = (
        reportName: string,
        headers: Array<RiskAssessmentExportHeader>,
        riskAssessmentRequest: RiskAssessmentExportRequest,
        fileNameBase?: string
    ) => {
        return post(`/CustomListExport/RiskAssessmentExport`, getRiskAssessmentExportBody(reportName, headers, riskAssessmentRequest, fileNameBase));
    };

    // Unlike the other overviews there is no filter request to carry: the users page filters client-side, so the
    // export is just the on-screen row ids in order and the backend re-derives every value from the database.
    const exportUsersInBackground = (reportName: string, headers: Array<UserExportHeader>, rows: Array<string>, fileNameBase?: string) => {
        return post(`/CustomListExport/UserExport`, { rows, headers, reportName, fileName: getExportFileName(fileNameBase ?? reportName) });
    };

    const exportFrameworksInBackground = (reportName: string, headers: Array<FrameworkExportHeader>, rows: Array<string>, fileNameBase: string) => {
        return post(`/CustomListExport/FrameworkExport`, { rows, headers, reportName, fileName: getExportFileName(fileNameBase) });
    };

    const exportObservationsInBackground = (reportName: string, headers: Array<ObservationExportHeader>, rows: Array<string>) => {
        return post(`/CustomListExport/ObservationExport`, { rows, headers, reportName, fileName: getExportFileName(reportName) });
    };

    const exportLegalEntitiesInBackground = (
        reportName: string,
        headers: Array<LegalEntityExportHeader>,
        legalEntityTableRequest: LegalEntitySearchRequest
    ) => {
        return post(`/CustomListExport/LegalEntityExport`, getLegalEntityExportBody(reportName, headers, legalEntityTableRequest));
    };

    const exportIncidentLogsInBackground = (reportName: string, headers: Array<IncidentLogExportHeader>, incidentLogTableRequest: IncidentLogSearchRequest) => {
        return post(`/CustomListExport/IncidentLogExport`, getIncidentLogExportBody(reportName, headers, incidentLogTableRequest));
    };

    const exportProcessesInBackground = (reportName: string, headers: Array<ProcessExportHeader>, processTableRequest: ProcessSearchRequest) => {
        return post(`/CustomListExport/ProcessExport`, getProcessExportBody(reportName, headers, processTableRequest));
    };

    const exportProcessingActivitiesInBackground = (
        reportName: string,
        headers: Array<ProcessingActivityExportHeader>,
        processingActivityTableRequest: ProcessingActivitySearchRequest
    ) => {
        return post(`/CustomListExport/ProcessingActivityExport`, getProcessingActivityExportBody(reportName, headers, processingActivityTableRequest));
    };

    const exportSharingsInBackground = (reportName: string, headers: Array<SharingExportHeader>, sharingTableRequest: SharingSearchRequest) => {
        return post(`/CustomListExport/SharingExport`, getSharingExportBody(reportName, headers, sharingTableRequest));
    };

    return {
        exportListInstantly,
        exportListInBackground,
        exportAllDataInBackground,
        exportContractsInBackground,
        exportActivitiesInBackground,
        exportSystemsInBackground,
        exportPoliciesInBackground,
        exportRiskOperationalInBackground,
        exportRiskMitigationInBackground,
        exportRiskAssessmentInBackground,
        exportUsersInBackground,
        exportFrameworksInBackground,
        exportObservationsInBackground,
        exportLegalEntitiesInBackground,
        exportIncidentLogsInBackground,
        exportProcessingActivitiesInBackground,
        exportProcessesInBackground,
        exportSharingsInBackground,
    };

    function getSharingExportBody(reportName: string, headers: Array<SharingExportHeader>, sharingTableRequest: SharingSearchRequest) {
        return {
            sharingTableRequest: sharingTableRequest,
            headers: headers,
            reportName: reportName,
            fileName: getExportFileName(reportName),
        };
    }

    function getActivityExportBody(reportName: string, headers: Array<ActivityExportHeader>, activitiesSearchRequest: ActivitiesSearchRequest) {
        const fileName = getExportFileName(reportName);

        return {
            activityTableRequest: activitiesSearchRequest,
            headers: headers,
            reportName: reportName,
            fileName: fileName,
        };
    }

    function getContractExportBody(reportName: string, headers: Array<ContractExportHeader>, contractsTableRequest: ContractTableRequest) {
        const fileName = getExportFileName(reportName);

        return {
            contractTableRequest: contractsTableRequest,
            headers: headers,
            reportName: reportName,
            fileName: fileName,
            extractAllContractData: false,
        };
    }

    function getSystemExportBody(reportName: string, headers: Array<SystemExportHeader>, systemTableRequest: SystemTableRequest) {
        const fileName = getExportFileName(reportName);

        return {
            systemTableRequest: systemTableRequest,
            headers: headers,
            reportName: reportName,
            fileName: fileName,
        };
    }

    function getPolicyExportBody(reportName: string, headers: Array<PolicyExportHeader>, policyTableRequest: PolicyTableRequest) {
        const fileName = getExportFileName(reportName);

        return {
            policyTableRequest: policyTableRequest,
            headers: headers,
            reportName: reportName,
            fileName: fileName,
        };
    }

    function getRiskOperationalExportBody(
        reportName: string,
        headers: Array<RiskOperationalExportHeader>,
        riskOperationalTableRequest: RiskOperationalTableRequest
    ) {
        const fileName = getExportFileName(reportName);

        return {
            riskOperationalTableRequest: riskOperationalTableRequest,
            headers: headers,
            reportName: reportName,
            fileName: fileName,
        };
    }

    function getRiskMitigationExportBody(
        reportName: string,
        headers: Array<RiskMitigationExportHeader>,
        riskMitigationTableRequest: RiskMitigationFilterRequest
    ) {
        const fileName = getExportFileName(reportName);

        return {
            riskMitigationTableRequest: riskMitigationTableRequest,
            headers: headers,
            reportName: reportName,
            fileName: fileName,
        };
    }

    function getRiskAssessmentExportBody(
        reportName: string,
        headers: Array<RiskAssessmentExportHeader>,
        riskAssessmentRequest: RiskAssessmentExportRequest,
        fileNameBase?: string
    ) {
        const fileName = getExportFileName(fileNameBase ?? reportName);

        return {
            ...riskAssessmentRequest,
            headers: headers,
            reportName: reportName,
            fileName: fileName,
        };
    }

    function getLegalEntityExportBody(reportName: string, headers: Array<LegalEntityExportHeader>, legalEntityTableRequest: LegalEntitySearchRequest) {
        const fileName = getExportFileName(reportName);

        return {
            legalEntityTableRequest: legalEntityTableRequest,
            headers: headers,
            reportName: reportName,
            fileName: fileName,
        };
    }

    function getIncidentLogExportBody(reportName: string, headers: Array<IncidentLogExportHeader>, incidentLogTableRequest: IncidentLogSearchRequest) {
        const fileName = getExportFileName(reportName);

        return {
            incidentLogTableRequest: incidentLogTableRequest,
            headers: headers,
            reportName: reportName,
            fileName: fileName,
        };
    }

    function getProcessExportBody(reportName: string, headers: Array<ProcessExportHeader>, processTableRequest: ProcessSearchRequest) {
        const fileName = getExportFileName(reportName);

        return {
            processTableRequest: processTableRequest,
            headers: headers,
            reportName: reportName,
            fileName: fileName,
        };
    }

    function getProcessingActivityExportBody(
        reportName: string,
        headers: Array<ProcessingActivityExportHeader>,
        processingActivityTableRequest: ProcessingActivitySearchRequest
    ) {
        const fileName = getExportFileName(reportName);

        return {
            processingActivityTableRequest: processingActivityTableRequest,
            headers: headers,
            reportName: reportName,
            fileName: fileName,
        };
    }
}
